/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-console */
import { Chips } from '@gerdau/hefesto-mais-web';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import {
  DateRangeType,
  DateType
} from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { Button } from '@gerdau/hefesto/Button';
import { driver } from 'driver.js';
import { saveAs } from 'file-saver';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import theme from '../../../../assets/styles/theme/theme';
import EmitterAndReceiverSelector from '../../../../components/EmitterAndReceiverSelector/Facelift';
import { getStatesFromCustomerArray } from '../../../../components/EmitterAndReceiverSelector/Facelift/SoldToView';
import {
  EFilterTagTypes,
  IFilterTag
} from '../../../../components/FiltersTags';
import { MarketingCloudService } from '../../../../services/MarketingCloud';
import { ApplicationState } from '../../../../store';
import { ICustomer } from '../../../../store/ducks/customers/types';
import { EOrderTab } from '../../../../store/ducks/long-steel-orders/types';
import { nextOnboardingStep } from '../../../../store/ducks/new-long-steel-onboarding/actions';
import { NewLongSteelOrdersOnboardingSteps } from '../../../../store/ducks/new-long-steel-onboarding/types';
import { updateTableType } from '../../../../store/ducks/new-long-steel-orders/actions';
import {
  ELongSteelOrdersStatus,
  INewLongSteelOrderData,
  IOrdersInitialInputValues,
  SalesOrderItemTable
} from '../../../../store/ducks/new-long-steel-orders/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { saveUser } from '../../../../store/ducks/user/actions';
import {
  ECustomerTypeView,
  EUserPreferenceType
} from '../../../../store/ducks/user/types';
import {
  DateFieldContainer,
  HelperText,
  NoWrapChipContainer
} from '../../../Purchase/LongSteel/ShoppingCartMVP/style';
import { DateFieldWrapper, FiltersContainer, OrderFilterBlock } from './styles';

let hasTrackedOrdersFiltersView = false;

export interface IOrdersStatus {
  id: ELongSteelOrdersStatus;
  description: string;
  ballCollor?: string;
}

interface IOrdersFiltersProps {
  customerViewType: ECustomerTypeView;
  onCleanFilters(): void;
  loadOrders({
    states,
    customerIds,
    startDate,
    endDate
  }: {
    states: string[];
    customerIds: string[];
    startDate?: string;
    endDate?: string;
  }): void;
  isFiltered: boolean;
  setFilteredOrders(filteredOrders: INewLongSteelOrderData[]): void;
  onEmittersChange?(emitterIds: string[]): void;
}

const exportToXLSX = (tableHTML: string, fileName: string): void => {
  const table = document.createElement('table');
  table.innerHTML = tableHTML;

  const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(table);

  const wb: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Dados');

  const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

  const data: Blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });

  saveAs(data, `${fileName}.xlsx`);

  MarketingCloudService.sendEvent({
    type: 'long-steel-orders',
    downloadExcel: true
  });
};

const OrdersFilters: React.FC<IOrdersFiltersProps> = ({
  customerViewType,
  loadOrders,
  setFilteredOrders,
  onEmittersChange
}) => {
  const dispatch = useDispatch();

  const driverRef = useRef<any>(null);
  const userState = useSelector((state: ApplicationState) => state.user);
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const customersLoading = useSelector(
    (state: ApplicationState) => state.customers.loading
  );

  const customers = useSelector((state: ApplicationState) =>
    state.customers.customers.filter((customer) =>
      userState.roles.ordersViewCustomers.includes(customer.customerId)
    )
  );

  const { step: onboardingStep } = useSelector(
    (state: ApplicationState) => state.newLongSteelOnboarding
  );

  const onboardingPreference = userState.preferences.find(
    (p) => p.name === EUserPreferenceType.ORDER_ONBOARDING
  );

  const ordersState = useSelector(
    (state: ApplicationState) => state.newLongSteelOrders
  );

  useEffect(() => {
    const isHidden = onboardingPreference?.preferenceItems?.includes('hidden');

    if (
      onboardingStep === NewLongSteelOrdersOnboardingSteps.INITIAL &&
      !isHidden &&
      !customersLoading
    ) {
      const driverObj = driver({
        allowClose: false,
        showProgress: false,
        nextBtnText: 'Avançar',
        prevBtnText: 'Voltar',
        doneBtnText: 'Fechar',
        overlayColor: '#192D3880',
        overlayClickBehavior: () => null,
        onNextClick: (elem: any, step: any, options: any) => {
          const activeStep = (options.state.activeIndex ?? 0) + 1;
          const isCompleted =
            onboardingPreference?.preferenceItems?.includes('completed');

          dispatch(
            sendTagging({
              event: 'onboarding_next_step',
              onboarding_name: 'pedidos_acos_longos',
              onboarding_version: 'v1',
              step_id: activeStep,
              step_name: 'pre_carregamento',
              next_step_id: activeStep + 1,
              progress_percentage: (activeStep / 5) * 100,
              screen_state: 'pre_table_load',
              step_context: isCompleted
                ? 'fluxo_ja_realizado'
                : 'primeiro_acesso',
              user_access_type: isCompleted
                ? 'already_accessed'
                : 'first_access',
              interaction_type: 'botao_avancar'
            })
          );

          if (driverObj) {
            driverObj.moveNext();
          }
        },
        onPopoverRender: (popover) => {
          const isCompleted =
            onboardingPreference?.preferenceItems?.includes('completed');

          const stepIndicator = document.createElement('div');
          const activeStep = (driverObj.getActiveIndex() ?? 0) + 1;

          localStorage.setItem(
            '@long-steel-orders-onboarding-time',
            String(Date.now() ?? '')
          );

          stepIndicator.innerText = `${activeStep} de 5 dicas`;
          stepIndicator.style.color = theme.colors.grayDark4;
          stepIndicator.style.fontSize = '14px';
          stepIndicator.style.lineHeight = '22px';
          stepIndicator.style.fontWeight = '400';
          stepIndicator.style.marginBottom = '8px';
          stepIndicator.style.fontFamily = theme.fonts.workSans;

          popover.title.parentElement?.prepend(stepIndicator);

          popover.wrapper.style.width = '448px';
          popover.wrapper.style.maxWidth = '448px';

          popover.title.style.color = theme.colors.grayDark3;
          popover.title.style.fontSize = '16px';
          popover.title.style.fontWeight = '600';
          popover.title.style.lineHeight = '24px';
          popover.title.style.fontFamily = theme.fonts.openSans;

          popover.description.style.color = theme.colors.grayDark4;
          popover.description.style.fontSize = '16px';
          popover.description.style.fontWeight = '400';
          popover.description.style.lineHeight = '24px';
          popover.description.style.marginTop = '8px';
          popover.description.style.marginBottom = '20px';
          popover.description.style.fontFamily = theme.fonts.workSans;

          const nextBtn = popover.nextButton;
          const prevBtn = popover.previousButton;

          [nextBtn, prevBtn].forEach((btn) => {
            if (btn) {
              btn.style.background = 'transparent';
              btn.style.border = 'none';
              btn.style.color = theme.colors.darkBlue2;
              btn.style.fontWeight = '400';
              btn.style.fontSize = '16px';
              btn.style.boxShadow = 'none';
              btn.style.textShadow = 'none';
              btn.style.fontFamily = theme.fonts.workSans;

              if (btn.disabled) {
                btn.style.fontFamily = theme.fonts.workSans;
                btn.style.color = theme.colors.darkBlue2;
                btn.style.opacity = '36%';
              }
            }
          });

          if (isCompleted) {
            const footer = popover.footer;
            const hideTipsBtn = document.createElement('button');
            hideTipsBtn.innerText = 'Ocultar dicas';
            hideTipsBtn.style.background = 'transparent';
            hideTipsBtn.style.border = 'none';
            hideTipsBtn.style.color = theme.colors.darkBlue2;
            hideTipsBtn.style.fontSize = '16px';
            hideTipsBtn.style.fontWeight = '400';
            hideTipsBtn.style.fontFamily = theme.fonts.workSans;
            hideTipsBtn.style.cursor = 'pointer';
            hideTipsBtn.style.marginRight = 'auto';
            hideTipsBtn.style.padding = '0';

            hideTipsBtn.onmousedown = (e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(
                sendTagging({
                  event: 'onboarding_closed',
                  onboarding_name: 'pedidos_acos_longos',
                  onboarding_version: 'v1',
                  step_id: activeStep,
                  step_name: 'pre_carregamento',
                  progress_percentage: (activeStep / 5) * 100,
                  screen_state: 'pre_table_load',
                  user_access_type: isCompleted
                    ? 'already_accessed'
                    : 'first_access',
                  interaction_type: 'botao_ocultar',
                  hide_tips_selected: true,
                  close_type: 'botao_ocultar_dicas'
                })
              );
              handleHideTips();
              driverObj.destroy();
            };

            footer.prepend(hideTipsBtn);
          }
        },
        steps: [
          {
            element: '#onboarding-order-type',
            popover: {
              title: 'Pedidos em aberto e faturados',
              description:
                'Agora você pode buscar seus pedidos em aberto e faturados por aqui.',
              side: 'right',
              align: 'center'
            }
          },
          {
            element: '#onboarding-search-button',
            popover: {
              title: 'Busca por pedidos em aberto retorna todos os resultados',
              description:
                'A busca por pedidos em aberto retorna todos os resultados e não necessita mais de seleção de períodos tornando sua análise ainda mais transparente.',
              side: 'bottom',
              align: 'center'
            }
          }
        ],
        onDestroyed: () => {
          driverRef.current = null;
        }
      });

      const isCompletedInit =
        onboardingPreference?.preferenceItems?.includes('completed');

      if (!hasTrackedOrdersFiltersView) {
        dispatch(
          sendTagging({
            event: 'onboarding_view',
            onboarding_name: 'pedidos_acos_longos',
            onboarding_version: 'v1',
            step_id: 1,
            step_name: 'pre_carregamento',
            next_step_id: 2,
            progress_percentage: (1 / 5) * 100,
            screen_state: 'pre_table_load',
            step_context: isCompletedInit
              ? 'fluxo_ja_realizado'
              : 'primeiro_acesso',
            user_access_type: isCompletedInit
              ? 'already_accessed'
              : 'first_access'
          })
        );
        hasTrackedOrdersFiltersView = true;
      }

      driverRef.current = driverObj;
      driverObj.drive();

      return () => {
        driverObj.destroy();
        driverRef.current = null;
      };
    }
    return () => null;
  }, [customersLoading, onboardingStep, onboardingPreference, dispatch]);

  const handleHideTips = () => {
    dispatch(
      saveUser({
        user: userState,
        userPreference: {
          name: EUserPreferenceType.ORDER_ONBOARDING,
          preferenceItems: ['hidden']
        }
      })
    );
  };

  const orderState = useSelector(
    (state: ApplicationState) => state.newLongSteelOrders
  );

  const activeTabState = orderState[orderState.tableType || EOrderTab.OPEN];

  const [orderStateDate, setOrderStateDate] = React.useState<DateRangeType>([
    activeTabState.initialInputValues.startDate
      ? moment(activeTabState.initialInputValues.startDate).toDate()
      : moment().subtract(1, 'months').toDate(),
    activeTabState.initialInputValues.endDate
      ? moment(activeTabState.initialInputValues.endDate).toDate()
      : moment().toDate()
  ]);

  const [selectedStates, setSelectedStates] = useState<string[]>(
    activeTabState.initialInputValues.states.length === 0
      ? getStatesFromCustomerArray(customers).map((item) => item.value)
      : activeTabState.initialInputValues.states
  );
  const [emitterIds, setEmitterIds] = useState<string[]>(
    activeTabState.initialInputValues.emitterIds.length == 0
      ? customers.map((customer) => customer.customerId)
      : activeTabState.initialInputValues.emitterIds
  );
  const [receiverId, setReceiverId] = useState('');

  const [startDate, setStartDate] = useState<Date | null>(
    activeTabState.initialInputValues.startDate
      ? moment(activeTabState.initialInputValues.startDate).toDate()
      : moment().subtract(30, 'days').toDate()
  );
  const [minStartDate, setMinStartDate] = useState<Date | undefined>(undefined);
  const [maxStartDate, setMaxStartDate] = useState<Date | null>(
    activeTabState.initialInputValues.endDate
      ? moment(activeTabState.initialInputValues.endDate).toDate()
      : new Date()
  );

  const [endDate, setEndDate] = useState<Date | null>(
    activeTabState.initialInputValues.endDate
      ? moment(activeTabState.initialInputValues.endDate).toDate()
      : new Date()
  );
  const [minEndDate, setMinEndDate] = useState(startDate);
  const [maxEndDate, setMaxEndDate] = useState(new Date());

  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const [ordersStatus, setOrdersStatus] = useState<ELongSteelOrdersStatus>(
    ELongSteelOrdersStatus.ALL
  );

  const [alreadyInitialized, setAlreadyInitialized] = useState(false);

  const ordersStatusList: IOrdersStatus[] = [
    {
      id: ELongSteelOrdersStatus.SHIPMENT,
      description: 'Em carregamento',
      ballCollor: theme.colors.blue
    },
    {
      id: ELongSteelOrdersStatus.DELIVERY,
      description: 'Em remessa',
      ballCollor: theme.colors.orange
    },
    {
      id: ELongSteelOrdersStatus.BILLED,
      description: 'Faturado',
      ballCollor: theme.colors.green
    },
    {
      id: ELongSteelOrdersStatus.PENDING,
      description: 'Saldo',
      ballCollor: theme.colors.gray3
    },
    // { id: EOrdersStatus.CANCELLED, description: 'Cancelado', ballCollor: theme.colors.red },
    { id: ELongSteelOrdersStatus.ALL, description: 'Todos' }
  ];

  const [tagsFilter, setTagsFilter] = useState<IFilterTag[]>([]);

  const updateTagsFilter = (
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    const newTagsFilter = tagsFilter.filter((tag) => !tag.canBeRemoved);
    const initialValues = activeTabState.initialInputValues;

    if (customerViewType === ECustomerTypeView.EMITTER) {
      if (
        receiverId !== '' &&
        orderState.receivers &&
        orderState.receivers.length > 1
      ) {
        const customer = orderState.receivers.find(
          (receiver: { customerId: string }) =>
            receiver.customerId === receiverId
        );
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.RECEIVER,
          filterText: `${customer?.customerId} - ${customer?.name}`,
          canBeRemoved: true
        });
      }
    } else if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      if (
        emitterIds.length > 0 &&
        orderState.emitters &&
        orderState.emitters.length > 1
      ) {
        const customer = orderState.emitters.find(
          (receiver: { customerId: string }) =>
            receiver.customerId === emitterIds[0]
        );
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.EMITTER,
          filterText: `${customer?.customerId} - ${customer?.name}`,
          canBeRemoved: true
        });
      }
    }

    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');
    if (
      startDate !== initialValues.startDate ||
      endDate !== initialValues.endDate
    ) {
      newTagsFilter.push({
        filterTagType: EFilterTagTypes.DATE,
        filterText: `${startSelectedDate.format(
          'DD/MM/YYYY'
        )} - ${endSelectedDate.format('DD/MM/YYYY')}`,
        canBeRemoved: true
      });
    }

    if (orderStatus !== ELongSteelOrdersStatus.ALL) {
      const statusDescription = ordersStatusList.find(
        (osl) => osl.id === orderStatus
      )?.description;
      newTagsFilter.push({
        filterTagType: EFilterTagTypes.STATUS,
        filterText: statusDescription as string,
        canBeRemoved: true
      });
    }

    if (selectedDivisions.length > 0) {
      selectedDivisions.forEach((division) => {
        const divisionDescription = orderState.divisions.find(
          (div: { division: string }) => div.division === division
        )?.divisionDescription;
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.PRODUCT_TYPE,
          filterText: divisionDescription as string,
          canBeRemoved: true
        });
      });
    }

    if (searchInput !== '') {
      newTagsFilter.push({
        filterTagType: EFilterTagTypes.SEARCH_INPUT,
        filterText: searchInput,
        canBeRemoved: true
      });
    }

    setTagsFilter(newTagsFilter);
  };

  const onChangeStates = (states: string[]) => {
    const selectedCustomers = customers.filter((cust) =>
      emitterIds.includes(cust.customerId)
    );

    if (
      selectedCustomers.some((cust) => !states.includes(cust.locality.state))
    ) {
      setEmitterIds([]);
      setReceiverId('');
    }

    setSelectedStates(states);
  };

  // useEffect(() => {
  //   const customersAvailableOnState = customers.filter((cust) =>
  //     selectedStates.includes(cust.locality.state)
  //   );
  //   if (
  //     !orderState.loading &&
  //     !userState.isManager &&
  //     emitterIds &&
  //     emitterIds.length === 1 &&
  //     customersAvailableOnState &&
  //     customersAvailableOnState.length === 1
  //   ) {
  //     handleFilter();
  //   }
  // }, [emitterIds]);

  useEffect(() => {
    const setInitialInputValues = (
      initialInputValues: IOrdersInitialInputValues
    ) => {
      if (initialInputValues.states.length > 0) {
        setSelectedStates(initialInputValues.states);
      } else {
        setSelectedStates(
          getStatesFromCustomerArray(customers).map((item) => item.value)
        );
      }

      if (initialInputValues.emitterIds.length > 0) {
        setEmitterIds(initialInputValues.emitterIds);
      } else {
        const statesToFilter =
          initialInputValues.states.length > 0
            ? initialInputValues.states
            : getStatesFromCustomerArray(customers).map((item) => item.value);

        setEmitterIds(
          customers
            .filter((cust) => statesToFilter.includes(cust.locality.state))
            .map((cust) => cust.customerId)
        );
      }

      setReceiverId(initialInputValues.receiverId);
      setOrdersStatus(initialInputValues.orderStatus);
      setAlreadyInitialized(true);
      initTagsFilter(initialInputValues);
    };

    const initTagsFilter = (initialInputValues: IOrdersInitialInputValues) => {
      const newTagsFilter: IFilterTag[] = [];
      const statesToUse =
        initialInputValues.states.length > 0
          ? initialInputValues.states
          : selectedStates;

      if (statesToUse.length > 0) {
        newTagsFilter.push({
          filterTagType: EFilterTagTypes.STATE,
          filterText: `(${statesToUse.length})`
        });
      }

      const getCustomerNameToTag = (customer: ICustomer): string => {
        const name: string[] = [];
        customer.name.split(' ').forEach((text) => {
          if (name.join(' ').length < 5) {
            name.push(text);
          }
        });
        return name.join(' ');
      };

      const emitterIdsToUse =
        initialInputValues.emitterIds.length > 0
          ? initialInputValues.emitterIds
          : emitterIds;

      if (customerViewType === ECustomerTypeView.EMITTER) {
        if (emitterIdsToUse.length > 0) {
          emitterIdsToUse.forEach((emitterId: string) => {
            const customer = customers.find(
              (cust) => cust.customerId === emitterId
            );
            if (customer) {
              newTagsFilter.push({
                filterTagType: EFilterTagTypes.EMITTER,
                filterText: `${customer.customerId} - ${getCustomerNameToTag(
                  customer
                )}`
              });
            }
          });
        }
      } else if (
        customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
      ) {
        const receiverIdToUse = initialInputValues.receiverId || receiverId;
        if (receiverIdToUse !== '') {
          const customer = customers.find(
            (cust) => cust.customerId === receiverIdToUse
          );
          if (customer) {
            newTagsFilter.push({
              filterTagType: EFilterTagTypes.RECEIVER,
              filterText: `${customer.customerId} - ${getCustomerNameToTag(
                customer
              )}`
            });
          }
        }
      }

      setTagsFilter(newTagsFilter);
    };

    if (
      activeTabState.initialInputValues &&
      !alreadyInitialized &&
      !orderState.loading &&
      activeTabState.initialized &&
      customers.length > 0
    ) {
      setInitialInputValues(activeTabState.initialInputValues);
    }
  }, [
    activeTabState.initialInputValues,
    orderState.loading,
    activeTabState.initialized,
    alreadyInitialized,
    customers,
    customerViewType,
    selectedStates,
    emitterIds,
    receiverId
  ]);

  const onFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment
  ) => {
    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');

    const hasChangedDate =
      activeTabState.initialInputValues.startDate !== startDate ||
      activeTabState.initialInputValues.endDate !== endDate;

    if (hasChangedDate || hasChangedEmitterIds() || hasChangedReceiverId()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders({ states, customerIds, startDate, endDate });
    }
  };

  const hasChangedEmitterIds = (): boolean => {
    const initialEmitterIds =
      activeTabState?.initialInputValues?.emitterIds || [];
    const hasNewEmitter = emitterIds.some(
      (emitter) => !initialEmitterIds.includes(emitter)
    );

    return hasNewEmitter || emitterIds.length !== initialEmitterIds.length;
  };

  const hasChangedReceiverId = (): boolean => {
    const initialReceiverId =
      activeTabState?.initialInputValues?.receiverId || '';

    return receiverId !== '' && receiverId !== initialReceiverId;
  };

  const filterByReceivers = (
    orders: INewLongSteelOrderData[]
  ): INewLongSteelOrderData[] => {
    return orders.filter((order) => order.shipTo.code === receiverId);
  };

  const filterByEmitters = (
    orders: INewLongSteelOrderData[]
  ): INewLongSteelOrderData[] => {
    const emitterId = emitterIds[0];
    return orders.filter((order) => order.soldTo.code === emitterId);
  };

  const canLoadOrders = (): boolean => {
    return (
      isUserAdmin ||
      // orderState.items.length === 0 ||
      (customerViewType === ECustomerTypeView.EMITTER &&
        hasChangedEmitterIds()) ||
      ((customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
        hasChangedReceiverId())
    );
  };

  const onOpenOrdersFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    if (canLoadOrders()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders({ states, customerIds });
      return;
    }

    const filterByDate = (
      orders: SalesOrderItemTable[]
    ): SalesOrderItemTable[] => {
      return orders.filter((order) => {
        const orderDate = moment(order.requestedDate, 'YYYY-MM-DD');
        return (
          orderDate.isSameOrAfter(startSelectedDate) &&
          orderDate.isSameOrBefore(endSelectedDate)
        );
      });
    };

    const filterByStatus = (
      orders: SalesOrderItemTable[]
    ): SalesOrderItemTable[] => {
      return orders.filter((order) => {
        switch (orderStatus) {
          case ELongSteelOrdersStatus.PENDING: {
            const pending = order.quantities.pendingQuantity
              ? Number(order.quantities.pendingQuantity)
              : 0;
            return pending && pending > 0;
          }

          case ELongSteelOrdersStatus.DELIVERY: {
            const delivery = order.quantities.deliveryQuantity
              ? Number(order.quantities.deliveryQuantity)
              : 0;
            return delivery && delivery > 0;
          }

          case ELongSteelOrdersStatus.SHIPMENT: {
            const shipment = order.quantities.shipmentQuantity
              ? Number(order.quantities.shipmentQuantity)
              : 0;
            return shipment && shipment > 0;
          }

          case ELongSteelOrdersStatus.BILLED: {
            const billed = order.quantities.billedQuantity
              ? Number(order.quantities.billedQuantity)
              : 0;
            return billed && billed > 0;
          }

          default:
            return true;
        }
      });
    };

    const filterBySearchInput = (
      orders: SalesOrderItemTable[]
    ): SalesOrderItemTable[] => {
      return orders.filter((order) => {
        return (
          order.sku.toString().toUpperCase().includes(searchInput) ||
          order.customerSKU.toUpperCase().includes(searchInput) ||
          order.customerOrder.toUpperCase().includes(searchInput) ||
          order.salesOrder.toString().toUpperCase().includes(searchInput) ||
          order.materialDescription.toUpperCase().includes(searchInput)
        );
      });
    };

    const filterByDivision = (
      orders: SalesOrderItemTable[]
    ): SalesOrderItemTable[] => {
      return orders.filter((order) =>
        selectedDivisions.includes(order.division)
      );
    };

    let filtered: SalesOrderItemTable[] =
      activeTabState.items as SalesOrderItemTable[];

    filtered = filterByDate(filtered);

    if (customerViewType === ECustomerTypeView.EMITTER && receiverId) {
      filtered = filterByReceivers(filtered) as SalesOrderItemTable[];
    }

    if (
      (customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
      emitterIds.length > 0
    ) {
      filtered = filterByEmitters(filtered) as SalesOrderItemTable[];
    }

    if (searchInput !== '') {
      filtered = filterBySearchInput(filtered);
    }

    if (orderStatus !== ELongSteelOrdersStatus.ALL) {
      filtered = filterByStatus(filtered);
    }

    if (selectedDivisions.length > 0) {
      filtered = filterByDivision(filtered);
    }

    setFilteredOrders(filtered);
    updateTagsFilter(
      emitterIds,
      receiverId,
      startSelectedDate,
      endSelectedDate,
      orderStatus,
      selectedDivisions,
      searchInput
    );
  };

  const onBilledOrdersFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');

    if (canLoadOrders()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders({ states, customerIds, startDate, endDate });
    } else {
      const filterBySearchInput = (
        orders: SalesOrderItemTable[]
      ): SalesOrderItemTable[] => {
        return orders.filter((order) => {
          return (
            order.sku.toString().toUpperCase().includes(searchInput) ||
            order.customerSKU.toUpperCase().includes(searchInput) ||
            order.salesOrder.toString().toUpperCase().includes(searchInput) ||
            order.customerOrder.toUpperCase().includes(searchInput) ||
            order.materialDescription.toUpperCase().includes(searchInput)
          );
        });
      };

      const filterByDivision = (
        orders: SalesOrderItemTable[]
      ): SalesOrderItemTable[] => {
        return orders.filter((order) =>
          selectedDivisions.includes(order.division)
        );
      };

      let filtered: SalesOrderItemTable[] =
        activeTabState.items as SalesOrderItemTable[];

      if (customerViewType === ECustomerTypeView.EMITTER && receiverId) {
        filtered = filterByReceivers(filtered) as SalesOrderItemTable[];
      }

      if (
        (customerViewType === ECustomerTypeView.RECEIVER ||
          customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
        emitterIds.length > 0
      ) {
        filtered = filterByEmitters(filtered) as SalesOrderItemTable[];
      }

      if (selectedDivisions.length > 0) {
        filtered = filterByDivision(filtered);
      }

      if (searchInput !== '') {
        filtered = filterBySearchInput(filtered);
      }

      setFilteredOrders(filtered);
      updateTagsFilter(
        emitterIds,
        receiverId,
        startSelectedDate,
        endSelectedDate,
        ELongSteelOrdersStatus.ALL,
        selectedDivisions,
        searchInput
      );
    }
  };

  const datePickerMaxDate = () => {
    return moment().toDate();
  };

  const datePickerMinDate = () => {
    if (orderStateDate?.[0]) {
      return moment(orderStateDate?.[0]).subtract(3, 'months').toDate();
    }

    if (orderStateDate?.[1]) {
      return moment(orderStateDate?.[1]).subtract(3, 'months').toDate();
    }
  };

  const handleDateChange = (date: DateRangeType) => {
    if (date && date[0] && date[1]) {
      // Data inicial não pode ser depois da data final
      if (moment(date[0]).isAfter(moment(date[1]))) {
        setOrderStateDate([date[0], null]);
        return;
      }

      // Data inicial não ter intervalo maior do que 3 meses da data final
      if (
        moment(date[0]).isBefore(
          moment(date[1]).subtract(3, 'months').subtract(1, 'day')
        )
      ) {
        setOrderStateDate([date[0], null]);
        return;
      }
    }
    setOrderStateDate(date);
  };

  const handleFilter = () => {
    if (onboardingStep === NewLongSteelOrdersOnboardingSteps.INITIAL) {
      if (driverRef.current) {
        return null;
      }
      dispatch(nextOnboardingStep());
    }
    let filterStartDate =
      orderState.tableType === EOrderTab.BILLED
        ? startDate
        : orderStateDate?.[0] || moment().subtract(1, 'months').toDate();
    let filterEndDate =
      orderState.tableType === EOrderTab.BILLED
        ? endDate
        : orderStateDate?.[1] || moment().toDate();

    if (orderState.tableType === EOrderTab.BILLED) {
      if (!filterStartDate && !filterEndDate) return;

      if (filterStartDate && filterEndDate) {
        const start = moment(filterStartDate);
        const end = moment(filterEndDate);

        if (
          end.isBefore(start) ||
          end.isAfter(start.clone().add(3, 'months'))
        ) {
          const newEndDate = start.clone().add(3, 'months').toDate();
          const finalEndDate =
            newEndDate < new Date() ? newEndDate : new Date();
          filterEndDate = finalEndDate;
          setEndDate(finalEndDate);
        }
      } else if (filterEndDate && !filterStartDate) {
        const newStartDate = moment(filterEndDate)
          .subtract(3, 'months')
          .toDate();
        filterStartDate = newStartDate;
        setStartDate(newStartDate);
      } else if (filterStartDate && !filterEndDate) {
        const newEndDate = moment(filterStartDate).add(3, 'months').toDate();
        const finalEndDate = newEndDate < new Date() ? newEndDate : new Date();
        filterEndDate = finalEndDate;
        setEndDate(finalEndDate);
      }
    }

    const customerId = emitterIds[0];
    const formatedCustomerId = [];
    userState.isManager && customerId.length < 10
      ? (formatedCustomerId[0] = `0${customerId}`)
      : (formatedCustomerId[0] = customerId);

    onFilter(
      selectedStates,
      userState.isManager ? formatedCustomerId : emitterIds,
      receiverId,
      moment(filterStartDate),
      moment(filterEndDate)
    );

    onEmittersChange?.(userState.isManager ? formatedCustomerId : emitterIds);

    dispatch(
      sendTagging({
        event: 'buscar_pedidos',
        screenName: 'LG:MeusPedidos',
        itemDescription: 'meus_pedidos_acosLongos',
        estado_emissor: selectedStates.join(', '),
        tipo_pedido:
          ordersState.tableType === EOrderTab.OPEN ? 'Aberto' : 'Faturado',
        emissor_pedido: customers
          .filter((item) => emitterIds.includes(item.customerId))
          .map((item) => item.name)
          .join(', '),
        data_inicio:
          ordersState.tableType === EOrderTab.OPEN
            ? undefined
            : moment(filterStartDate).format('DD/MM/YYYY'),
        data_fim:
          ordersState.tableType === EOrderTab.OPEN
            ? undefined
            : moment(filterEndDate).format('DD/MM/YYYY')
      })
    );
  };

  const isEndDateValid =
    orderState.tableType === EOrderTab.BILLED
      ? !!startDate || !!endDate
      : !!orderStateDate?.[1];

  const disabledButton =
    orderState.loading ||
    !isEndDateValid ||
    (customerViewType === ECustomerTypeView.RECEIVER ||
    customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
      ? receiverId.length === 0
      : emitterIds.filter((i) => i).length === 0) ||
    (!userState.isManager && selectedStates.length === 0);

  return (
    <FiltersContainer>
      <OrderFilterBlock
        hasBilledTableType={orderState.tableType === EOrderTab.BILLED}
      >
        <NoWrapChipContainer id="onboarding-order-type">
          <label className="hefesto-input-wrapper hefesto-input-wrapper__label-wrapper__label">
            Tipo de pedido
          </label>
          <Chips
            label={'Tipo de pedido'}
            chips={[
              { id: EOrderTab.OPEN, label: 'Aberto' },
              { id: EOrderTab.BILLED, label: 'Faturado' }
            ]}
            type="choice"
            handleSelect={(selected: string[]) => {
              if (
                selected &&
                selected.length > 0 &&
                selected[0] !== orderState.tableType
              ) {
                dispatch(
                  updateTableType({
                    tableType: selected[0] as EOrderTab
                  })
                );
              }
            }}
            selectedChipIds={[orderState.tableType]}
          />
        </NoWrapChipContainer>

        <EmitterAndReceiverSelector
          orderOrInvoiceState={orderState}
          customerViewType={customerViewType}
          isUserAdmin={isUserAdmin}
          emitterIds={emitterIds}
          setEmitterIds={setEmitterIds}
          receiverId={receiverId}
          setReceiverId={setReceiverId}
          customers={customers}
          showCustomerStates={true}
          selectedStates={selectedStates}
          onChangeStates={onChangeStates}
          customersPlaceholder="Selecione"
          statesPlaceholder="Selecione"
          onAdminSearch={(id) => {
            const formattedId = id.padStart(10, '0');
            setEmitterIds([formattedId]);
            loadOrders({ states: selectedStates, customerIds: [formattedId] });
          }}
        />

        {orderState.tableType === EOrderTab.BILLED && (
          <DateFieldWrapper>
            <label className="hefesto-input-wrapper hefesto-input-wrapper__label-wrapper__label">
              Período de faturamento
            </label>
            <DateFieldContainer>
              <DateField
                label=""
                hideLabel={true}
                minDate={minStartDate ?? undefined}
                maxDate={maxStartDate ?? undefined}
                name="startDate"
                onChange={(value: DateType) => {
                  if (!value) {
                    setStartDate(null);
                    return;
                  }
                  const time = value.getTime();
                  if (time) {
                    const newStartDate = new Date(time);
                    setStartDate(newStartDate);
                  }
                }}
                selected={startDate ?? undefined}
                selectsRange={false}
              />
              <DateField
                label=""
                hideLabel={true}
                minDate={undefined}
                maxDate={maxStartDate ?? undefined}
                name="endDate"
                onChange={(value: DateType) => {
                  if (!value) {
                    setEndDate(null);
                    return;
                  }
                  const time = value.getTime();
                  if (time) {
                    const newEndDate = new Date(time);
                    setEndDate(newEndDate);
                  }
                }}
                selected={endDate ?? undefined}
                selectsRange={false}
              />
            </DateFieldContainer>
            <HelperText className="hefesto-input-wrapper__label-wrapper__label">
              Selecione um período de até 3 meses.
            </HelperText>
          </DateFieldWrapper>
        )}

        <Button
          id="onboarding-search-button"
          onClick={handleFilter}
          name="buscar-filtros"
          disabled={disabledButton || orderState.loading}
          style={{ alignSelf: 'flex-start', marginTop: '24px' }}
        >
          Buscar pedidos
        </Button>
      </OrderFilterBlock>
    </FiltersContainer>
  );
};

export default OrdersFilters;
